import { useState, useEffect, createContext, useContext } from "react";
import { authService } from "../services/authService";
import { db } from "../lib/supabase";
import { useApp } from "../context/AppContext";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { dispatch } = useApp();

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsInitializing(true);
      console.log('Initializing auth...');
      
      const currentUser = await authService.initialize();
      console.log('Auth initialized, user:', currentUser?.email);

      if (currentUser) {
        setUser(currentUser);
        console.log('Loading profile for user:', currentUser.id);
        await loadUserProfile(currentUser.id);
      } else {
        console.log('No current user found');
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      setError(error.message);
    } finally {
      setIsInitializing(false);
      console.log('Auth initialization complete');
    }
  };

  const loadUserProfile = async (userId) => {
    try {
      console.log('Loading user profile for ID:', userId);
      const userProfile = await db.getProfile(userId);
      console.log('Profile loaded:', userProfile);
      setProfile(userProfile);

      // Dispatch user profile metadata to global AppContext
      dispatch({
        type: "SET_USER_DATA",
        payload: {
          name: userProfile.name || "",
          age: userProfile.age || "",
          timezone: userProfile.timezone || "UTC",
          sleepSchedule: userProfile.sleep_schedule || { bedtime: "22:00", wakeup: "07:00" },
          reminderSettings: userProfile.reminder_settings || { hydration: true, sleep: true, selfCare: true, checkIn: true },
          interests: userProfile.interests || [],
        }
      });

      if (userProfile.onboarding_completed) {
        dispatch({ type: "COMPLETE_ONBOARDING" });
      }

      // These values are useful after the first render, but none of them should
      // keep the whole application on the startup screen. Start all requests at
      // once and apply each result as soon as it arrives.
      const loadCreditWallet = async () => {
        try {
          const wallet = await db.getCreditWallet(userId);
          if (wallet && authService.currentUser?.id === userId) {
            dispatch({ type: "SET_USER_DATA", payload: { credits: wallet.balance } });
          }
        } catch (walletError) {
          console.warn("Credit wallet not loaded:", walletError.message);
        }
      };

      const loadAITwin = async () => {
        try {
          const twins = await db.getAITwins(userId);
          if (twins?.length > 0 && authService.currentUser?.id === userId) {
            const activeTwin = twins[0];
            dispatch({
              type: "SET_TWIN_DATA",
              payload: {
                id: activeTwin.id,
                name: activeTwin.name,
                personality: activeTwin.personality,
                relationship: activeTwin.relationship || activeTwin.relationship_type,
                interests: activeTwin.interests || [],
                voiceId: activeTwin.voice_settings?.voiceId || "default",
              },
            });
          }
        } catch (twinError) {
          console.warn("Failed to load AI twins:", twinError.message);
        }
      };

      const loadMoodHistory = async () => {
        try {
          const moods = await db.getMoodEntries(userId);
          if (moods?.length > 0 && authService.currentUser?.id === userId) {
            const formattedHistory = moods.map(entry => {
              const hr = parseInt(entry.tags?.find(t => t.startsWith("heart_rate:"))?.split(":")[1]) || 72;
              const ss = parseInt(entry.tags?.find(t => t.startsWith("stress:"))?.split(":")[1]) || 40;
              const rs = parseInt(entry.tags?.find(t => t.startsWith("recovery:"))?.split(":")[1]) || 60;
              return {
                mood: entry.mood,
                timestamp: entry.created_at,
                date: new Date(entry.created_at).toDateString(),
                note: entry.note || "",
                metadata: {
                  heartRate: hr,
                  stressScore: ss,
                  recoveryScore: rs
                }
              };
            }).reverse();

            dispatch({
              type: "LOAD_STATE",
              payload: {
                currentMood: moods[0].mood,
                mood: {
                  current: moods[0].mood,
                  history: formattedHistory,
                  lastCheckIn: moods[0].created_at
                }
              }
            });
          }
        } catch (moodError) {
          console.warn("Failed to load mood entries:", moodError.message);
        }
      };

      void Promise.allSettled([
        loadCreditWallet(),
        loadAITwin(),
        loadMoodHistory(),
      ]);
    } catch (error) {
      console.error("Failed to load user profile:", error);
      setProfile(null);
    }
  };

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      setError(null);

      const { user: newUser } = await authService.signUp(
        email,
        password,
        userData
      );

      if (newUser) {
        setUser(newUser);
        // Profile will be created automatically by the database trigger
        setTimeout(() => loadUserProfile(newUser.id), 1000);
      }

      return newUser;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const { user: signedInUser } = await authService.signIn(email, password);

      if (signedInUser) {
        setUser(signedInUser);
        await loadUserProfile(signedInUser.id);
      }

      return signedInUser;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await authService.signOut();
      setUser(null);
      setProfile(null);
      dispatch({ type: "RESET_STATE" });
      localStorage.removeItem("twinHeartData");
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error("No user logged in");

      const updatedProfile = await db.updateProfile(user?.id, updates);
      setProfile(updatedProfile);

      return updatedProfile;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    profile,
    loading,
    isInitializing,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    isAuthenticated: !!user,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
